import { useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/router";

// Types for telemetry events
export interface TelemetryEvent {
  name: string;
  properties?: Record<string, any>;
  tags?: Record<string, string>;
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
}

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 'bytes' | 'count' | 'percentage';
  tags?: Record<string, string>;
}

export interface BusinessEvent {
  event: string;
  revenue?: number;
  currency?: string;
  properties?: Record<string, any>;
}

// Core telemetry functions
export const telemetry = {
  // User context management (console logging for development)
  setUser: (user: { id?: string; email?: string; username?: string; [key: string]: any }) => {
    console.log('[Telemetry] Set user context:', {
      timestamp: new Date().toISOString(),
      user: {
        id: user.id,
        email: user.email,
        username: user.username,
        ...user,
      },
    });
  },

  // Clear user context (console logging for development)
  clearUser: () => {
    console.log('[Telemetry] Clear user context:', {
      timestamp: new Date().toISOString(),
      action: 'user_context_cleared',
    });
  },

  // Set custom tags (console logging for development)
  setTags: (tags: Record<string, string>) => {
    console.log('[Telemetry] Set tags:', {
      timestamp: new Date().toISOString(),
      tags,
    });
  },

  // Set custom context (console logging for development)
  setContext: (key: string, context: Record<string, any>) => {
    console.log('[Telemetry] Set context:', {
      timestamp: new Date().toISOString(),
      key,
      context,
    });
  },

  // Track custom events (console logging for development)
  track: (event: TelemetryEvent) => {
    console.log('[Telemetry] Track event:', {
      timestamp: new Date().toISOString(),
      event: event.name,
      category: 'user_action',
      properties: event.properties,
      tags: event.tags,
      level: 'info',
    });

    if (event.tags) {
      telemetry.setTags(event.tags);
    }

    if (event.user) {
      telemetry.setUser(event.user);
    }
  },

  // Track page views
  trackPageView: (page: string, properties?: Record<string, any>) => {
    telemetry.track({
      name: 'page_view',
      properties: {
        page,
        timestamp: new Date().toISOString(),
        ...properties,
      },
      tags: {
        page_type: 'navigation',
      },
    });
  },

  // Track user interactions
  trackInteraction: (element: string, action: string, properties?: Record<string, any>) => {
    telemetry.track({
      name: 'user_interaction',
      properties: {
        element,
        action,
        timestamp: new Date().toISOString(),
        ...properties,
      },
      tags: {
        interaction_type: action,
        element_type: element,
      },
    });
  },

  // Track form events
  trackForm: (formName: string, event: 'start' | 'submit' | 'error' | 'success', properties?: Record<string, any>) => {
    telemetry.track({
      name: 'form_event',
      properties: {
        form_name: formName,
        form_event: event,
        timestamp: new Date().toISOString(),
        ...properties,
      },
      tags: {
        form_name: formName,
        form_event: event,
      },
    });
  },

  // Track business events
  trackBusiness: (businessEvent: BusinessEvent) => {
    telemetry.track({
      name: 'business_event',
      properties: {
        event: businessEvent.event,
        revenue: businessEvent.revenue,
        currency: businessEvent.currency || 'USD',
        timestamp: new Date().toISOString(),
        ...businessEvent.properties,
      },
      tags: {
        business_event: businessEvent.event,
        has_revenue: businessEvent.revenue ? 'true' : 'false',
      },
    });

    // Set business context for revenue events
    if (businessEvent.revenue) {
      telemetry.setContext('revenue', {
        amount: businessEvent.revenue,
        currency: businessEvent.currency || 'USD',
        event: businessEvent.event,
      });
    }
  },

  // Track performance metrics (console logging for development)
  trackPerformance: (metric: PerformanceMetric) => {
    console.log('[Telemetry] Performance metric:', {
      timestamp: new Date().toISOString(),
      metric: metric.name,
      category: 'performance',
      value: metric.value,
      unit: metric.unit,
      tags: metric.tags,
      level: 'info',
    });

    // Alert on poor performance (console logging for development)
    if (metric.name === 'page_load_time' && metric.value > 3000) {
      console.warn('[Telemetry] Slow page load detected:', {
        timestamp: new Date().toISOString(),
        message: 'Slow page load detected',
        level: 'warning',
        tags: {
          performance_issue: 'slow_load',
          ...metric.tags,
        },
        metric_name: metric.name,
        value: metric.value,
        unit: metric.unit,
      });
    }
  },

  // Track errors with context (console logging for development)
  trackError: (error: Error, context?: Record<string, any>, tags?: Record<string, string>) => {
    console.error('[Telemetry] Error tracked:', {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      context,
      tags,
    });
  },

  // Track API calls
  trackApiCall: (
    endpoint: string,
    method: string,
    status: number,
    duration: number,
    properties?: Record<string, any>
  ) => {
    const isError = status >= 400;
    
    telemetry.track({
      name: 'api_call',
      properties: {
        endpoint,
        method,
        status,
        duration,
        success: !isError,
        timestamp: new Date().toISOString(),
        ...properties,
      },
      tags: {
        api_endpoint: endpoint,
        api_method: method,
        api_status: status.toString(),
        api_success: isError ? 'false' : 'true',
      },
    });

    // Alert on API errors or slow responses (console logging for development)
    if (isError) {
      console.error('[Telemetry] API Error:', {
        timestamp: new Date().toISOString(),
        message: `API Error: ${method} ${endpoint}`,
        level: 'error',
        tags: {
          api_error: 'true',
          endpoint,
          method,
          status: status.toString(),
        },
        duration,
        ...properties,
      });
    } else if (duration > 2000) {
      console.warn('[Telemetry] Slow API Response:', {
        timestamp: new Date().toISOString(),
        message: `Slow API Response: ${method} ${endpoint}`,
        level: 'warning',
        tags: {
          api_performance: 'slow',
          endpoint,
          method,
        },
        duration,
        status,
        ...properties,
      });
    }
  },
};

// React hooks for telemetry
export const useTelemetry = () => {
  const router = useRouter();

  // Track page views automatically
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      telemetry.trackPageView(url, {
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    
    // Track initial page view
    handleRouteChange(router.asPath);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  return telemetry;
};

// Hook for tracking form interactions
export const useFormTelemetry = (formName: string) => {
  const formStarted = useRef(false);

  const trackFormStart = useCallback(() => {
    if (!formStarted.current) {
      telemetry.trackForm(formName, 'start');
      formStarted.current = true;
    }
  }, [formName]);

  const trackFormSubmit = useCallback((properties?: Record<string, any>) => {
    telemetry.trackForm(formName, 'submit', properties);
  }, [formName]);

  const trackFormError = useCallback((error: string, properties?: Record<string, any>) => {
    telemetry.trackForm(formName, 'error', { error, ...properties });
  }, [formName]);

  const trackFormSuccess = useCallback((properties?: Record<string, any>) => {
    telemetry.trackForm(formName, 'success', properties);
  }, [formName]);

  return {
    trackFormStart,
    trackFormSubmit,
    trackFormError,
    trackFormSuccess,
  };
};

// Hook for tracking user interactions
export const useInteractionTelemetry = () => {
  const trackClick = useCallback((element: string, properties?: Record<string, any>) => {
    telemetry.trackInteraction(element, 'click', properties);
  }, []);

  const trackHover = useCallback((element: string, properties?: Record<string, any>) => {
    telemetry.trackInteraction(element, 'hover', properties);
  }, []);

  const trackFocus = useCallback((element: string, properties?: Record<string, any>) => {
    telemetry.trackInteraction(element, 'focus', properties);
  }, []);

  const trackScroll = useCallback((element: string, scrollDepth: number) => {
    telemetry.trackInteraction(element, 'scroll', { scroll_depth: scrollDepth });
  }, []);

  return {
    trackClick,
    trackHover,
    trackFocus,
    trackScroll,
  };
};

// Hook for performance monitoring
export const usePerformanceTelemetry = () => {
  useEffect(() => {
    // Track page load performance
    const trackPageLoad = () => {
      if (typeof window !== 'undefined' && window.performance) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        if (navigation) {
          telemetry.trackPerformance({
            name: 'page_load_time',
            value: navigation.loadEventEnd - navigation.fetchStart,
            unit: 'ms',
            tags: {
              page: window.location.pathname,
            },
          });

          telemetry.trackPerformance({
            name: 'dom_content_loaded',
            value: navigation.domContentLoadedEventEnd - navigation.fetchStart,
            unit: 'ms',
            tags: {
              page: window.location.pathname,
            },
          });

          telemetry.trackPerformance({
            name: 'first_byte_time',
            value: navigation.responseStart - navigation.fetchStart,
            unit: 'ms',
            tags: {
              page: window.location.pathname,
            },
          });
        }
      }
    };

    // Track performance when page is loaded
    if (document.readyState === 'complete') {
      trackPageLoad();
    } else {
      window.addEventListener('load', trackPageLoad);
      return () => window.removeEventListener('load', trackPageLoad);
    }
  }, []);
};

// Business intelligence utilities
export const businessTelemetry = {
  // Track contact form submissions
  trackContactForm: (formData: { name: string; email: string; phone?: string; message: string }) => {
    telemetry.trackBusiness({
      event: 'contact_form_submission',
      properties: {
        form_type: 'contact',
        has_phone: !!formData.phone,
        message_length: formData.message.length,
        source: 'website',
      },
    });
  },

  // Track career applications
  trackCareerApplication: (position?: string) => {
    telemetry.trackBusiness({
      event: 'career_application',
      properties: {
        position: position || 'general',
        source: 'website',
      },
    });
  },

  // Track file downloads
  trackFileDownload: (fileName: string, fileType: string) => {
    telemetry.trackBusiness({
      event: 'file_download',
      properties: {
        file_name: fileName,
        file_type: fileType,
        source: 'website',
      },
    });
  },

  // Track email clicks
  trackEmailClick: (emailType: string) => {
    telemetry.trackBusiness({
      event: 'email_click',
      properties: {
        email_type: emailType,
        source: 'website',
      },
    });
  },

  // Track phone calls
  trackPhoneCall: () => {
    telemetry.trackBusiness({
      event: 'phone_call_initiated',
      properties: {
        source: 'website',
      },
    });
  },
};

// Debug utilities for development
export const debugTelemetry = {
  // Log all events to console in development
  enableDebugMode: () => {
    if (process.env.NODE_ENV === 'development') {
      const originalTrack = telemetry.track;
      telemetry.track = (event: TelemetryEvent) => {
        console.log('🔍 Telemetry Event:', event);
        return originalTrack(event);
      };
    }
  },

  // Test telemetry setup
  testSetup: () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧪 Testing telemetry setup...');
      
      // Test basic tracking
      telemetry.track({
        name: 'telemetry_test',
        properties: { test: true },
      });

      // Test error tracking
      try {
        throw new Error('Test error for telemetry logging');
      } catch (error) {
        telemetry.trackError(error as Error, { test: true });
      }

      console.log('✅ Console telemetry test completed');
    }
  },
};
